import { Color } from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { DateTime } from "luxon";
import { DeviceCapabilities, DeviceStatus, DeviceType } from "./device.model";
import { config } from "../config";

export function hasDeviceCapability(
  deviceType: DeviceType,
  capability: DeviceCapabilities,
): boolean {
  switch (capability) {
    case DeviceCapabilities.RemoteAccess:
    case DeviceCapabilities.EditValue:
      return deviceType === DeviceType.Fidelix;
    default:
      return false;
  }
}

const DISCONNECTION_THRESHOLD_HOURS = 168; // 7 days

function isDisconnected(
  status: DeviceStatus,
  lastOnlineAt: DateTime | null,
): boolean {
  if (status === DeviceStatus.Connected || status === DeviceStatus.Disabled) {
    return false;
  }

  if (!lastOnlineAt || !lastOnlineAt.isValid) {
    return true;
  }

  return (
    DateTime.now().diff(lastOnlineAt, "hours").hours >
    DISCONNECTION_THRESHOLD_HOURS
  );
}

export function getDeviceStatusColor(
  status: DeviceStatus,
  lastOnlineAt: DateTime | null,
): Color {
  if (isDisconnected(status, lastOnlineAt)) {
    return "mutedDarkNeutral";
  }

  switch (status) {
    case DeviceStatus.Connected:
      return "success";
    case DeviceStatus.Disabled:
      return "warning";
    default:
      return "error";
  }
}

export function getDeviceStatusText(status?: DeviceStatus): string {
  switch (status) {
    case DeviceStatus.Connected:
      return t`Connected`;
    case DeviceStatus.Disabled:
      return t`Disabled`;
    default:
      return t`Disconnected`;
  }
}

export function getDeviceTooltipText(
  status: DeviceStatus,
  lastOnlineAt: DateTime | null,
  locale = "en",
): string {
  if (status === DeviceStatus.Connected || status === DeviceStatus.Disabled) {
    return getDeviceStatusText(status);
  }

  if (!lastOnlineAt || !lastOnlineAt.isValid) {
    return t`Connection has never been established`;
  }

  const localized = lastOnlineAt.setLocale(locale);
  const date = localized.toLocaleString(DateTime.DATE_SHORT);
  const time = localized.toLocaleString(DateTime.TIME_24_SIMPLE);

  return t`Disconnected from ${date}, ${time}`;
}

/**
 * Parse query parameter 'status' from the URL and convert it into a number array
 * This is done because selecting Disconnected selects multiple statuses at the same
 * time and this selection needs to be flattened into a single flat array
 */
export function parseStatusFromURL(status: string[]): number[] {
  return status.reduce<number[]>((a, c) => {
    const jsoned = JSON.parse(c);
    if (jsoned instanceof Array) {
      return [...a, ...jsoned.map(Number)];
    }
    return [...a, Number(c)];
  }, []);
}

export function getDeviceBelindaUrl(deviceId: number, companyId: string): URL {
  const webUiProxyUrl = new URL(
    `https://${deviceId}.${config.BELINDA_HOSTNAME}`,
  );
  webUiProxyUrl.searchParams.set("companyId", companyId);
  return webUiProxyUrl;
}
